<template>
  <StandardWrapper
    :blok="{ ...item, verticalPadding: 'none' }"
    v-bind="$attrs"
    class="job-alert-wrapper"
  >
    <div
      class="flex flex-col items-center justify-between py-4 sm:flex-row xl:px-4"
    >
      <div class="flex gap-2">
        <BellAlertIcon
          class="mt-1 h-5 min-h-5 w-5 min-w-5 sm:mt-0 sm:h-6 sm:w-6"
          :class="textColorClass(item.textVariant, item.bgVariant)"
        />
        <RichText
          class="text-base"
          :blok="item"
          :rich-text-content="item.text"
        />
      </div>

      <CallToActionButton
        class="mt-4 w-full sm:mt-0 sm:w-auto"
        :blok="item"
        :item="{ ...item.button[0], actionIntent: 'job-alert-modal' }"
      />
    </div>
  </StandardWrapper>

  <ModalWrapper
    show-on-action-intent="job-alert-modal"
    component="JobAlert"
    :blok="{ ...item, bgVariant: 'white' }"
    @modal-closed="reset()"
  >
    <h2
      class="title -mx-6 -mt-12 flex items-center gap-2 border-b px-4 pb-2 text-2xl leading-none text-gray-900 lg:-mx-4 lg:-mt-6 lg:text-xl"
    >
      <BellAlertIcon class="text-default h-8 w-8 lg:h-6 lg:w-6" />
      {{ item.modalTitle }}
    </h2>

    <RichText
      class="mt-6 text-base"
      :blok="{ ...item, textVariant: null }"
      :rich-text-content="item.modalText"
    />

    <div class="mt-6">
      <form :id="`form${item._uid}`" class="relative flex" @submit="checkForm">
        <div
          class="'flex lg:-mr-2' relative min-w-0 flex-1 flex-col gap-2 md:flex-row md:items-center md:gap-4"
        >
          <label :for="`email${item._uid}`" class="sr-only"
            >{{ item.placeholderText }}:</label
          >
          <input
            :id="`email${item._uid}`"
            ref="emailInput"
            v-model.trim="state.email"
            type="email"
            class="embedded order-1 block w-full border text-base text-gray-900 placeholder-gray-700 sm:text-sm"
            :placeholder="item.placeholderText"
            autocomplete="email"
            required
            :aria-invalid="v$.email?.$error"
            :aria-describedby="v$.email?.$error ? `alert${blok._uid}` : null"
            :class="inputClass"
            @keypress.enter="checkForm"
            @input="handleInput"
          />
        </div>
        <!-- Button -->
        <div class="relative mt-0 w-16">
          <button
            type="submit"
            class="btn relative"
            :class="[btnClass, isLoading ? 'cursor-wait' : '']"
            :disabled="alertCreated"
            @click.prevent="checkForm"
          >
            <div class="flex items-center justify-center">
              <template v-if="isLoading">
                <IconSpinner aria-hidden="true" class="my-0.5 sm:my-0" />
                <span class="sr-only">Bezig</span>
              </template>
              <div
                v-else
                class="flex flex-row-reverse items-center justify-center"
              >
                <component
                  :is="alertCreated ? CheckIcon : PaperAirplaneIcon"
                  class="h-6 w-6 sm:h-5 sm:w-5"
                />
                <span class="sr-only">Aanmaken</span>
              </div>
            </div>
          </button>
        </div>
      </form>
      <div
        class="relative block h-[3.5rem] opacity-0"
        :class="{
          'opacity-95': v$.email?.$error || showRequestError || alertCreated,
          'opacity-0': !v$.email?.$error,
        }"
      >
        <div
          class="z-10 mt-2 flex rounded outline outline-black/5"
          :class="alertCreated ? 'bg-green-50' : 'bg-yellow-50'"
        >
          <div class="z-20 flex px-2 py-1 sm:py-1.5">
            <span :class="alertCreated ? 'text-green-500' : 'text-yellow-500'">
              <component
                :is="alertCreated ? CheckCircleIcon : ExclamationCircleIcon"
                class="h-5 w-5"
              />
            </span>
            <button
              :tabindex="v$.email?.$error ? 0 : -1"
              :aria-hidden="!v$.email?.$error"
              class="ml-1 inline-block cursor-pointer text-left text-sm"
              :class="alertCreated ? 'text-green-800' : 'text-yellow-800'"
              @click="emailInput.focus()"
            >
              <span v-if="alertCreated">
                Succes! Uw job alert is ingesteld. Wanneer er nieuwe vacatures
                worden geplaatst, ontvangt u deze dagelijks per e-mail.
              </span>
              <span
                v-else-if="!showRequestError"
                :id="`alert${blok._uid}`"
                :role="v$.email?.$error ? 'alert' : null"
                aria-live="polite"
                aria-atomic="true"
              >
                {{ state.email ? invalidEmailMessage : requiredEmailMessage }}
              </span>
              <span
                v-else
                :id="`alert${blok._uid}`"
                :role="showRequestError ? 'alert' : null"
                aria-live="polite"
                aria-atomic="true"
              >
                Er is iets niet helemaal goed gegaan. Probeer het later nog
                eens.
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </ModalWrapper>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import ModalWrapper from './ModalWrapper.vue'
import RichText from './RichText.vue'
import CallToActionButton from './CallToActionButton.vue'
import StandardWrapper from './StandardWrapper.vue'
import { useColorPalette } from '../composables/colorPalette'
import { useVuelidate } from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import {
  CheckCircleIcon,
  CheckIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/vue/24/solid'
import IconSpinner from '../icons/IconSpinner.vue'
import { createJobAlert } from '../api/tooContentDelivery.js'
import { usePageContext } from '../renderer/usePageContext.js'
import { useHead } from '@unhead/vue'
import { BellAlertIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
  item: {
    type: Object,
    default: () => {},
  },
  searchQuery: {
    type: Object,
    default: () => {},
  },
})

const isLoading = ref(false)
const showRequestError = ref(false)
const alertCreated = ref(false)
const emailInput = ref(null)
const state = reactive({
  email: null,
})
const rules = {
  email: {
    required,
    email,
  },
}
const v$ = useVuelidate(rules, state)
const { textColorClass, colorName, colorCustomClass } = useColorPalette()

const requiredEmailMessage = computed(() => {
  return (
    props.blok.requiredEmailMessage ||
    'Het veld e-mailadres is niet ingevuld. Vul hier je e-mailadres in.'
  )
})
const invalidEmailMessage = computed(() => {
  return (
    props.blok.invalidEmailMessage ||
    'Dit is geen juist e-mailadres. Vul hier een geldig e-mailadres in, bijvoorbeeld naam@gmail.com'
  )
})

const inputClass = computed(() => {
  let classes = 'border-gray-300 rounded-l-md shadow-sm'

  const color = colorName(props.blok.color || 'default')
  classes += ` focus:border-transparent focus-${color}`

  return classes
})
const btnClass = computed(() => {
  const radiusClass = 'rounded-none rounded-r-md'

  let color = colorName('primary', null, '')
  if (props.item.button?.length) {
    color = colorName(props.item.button[0].color, null, '')
  }

  return `btn-style-primary btn-style-primary-color-${color} ${radiusClass}`
})

async function checkForm(event) {
  if (isLoading.value || alertCreated.value) {
    return
  }

  event && event.preventDefault()
  showRequestError.value = false
  state.email = state.email?.trim()
  v$.value.$touch()

  if (!v$.value.$invalid) {
    try {
      isLoading.value = true

      const searchQuery = {
        search_term: props.searchQuery.filter,
        discipline: props.searchQuery.discipline,
        education: props.searchQuery.education,
        work_category: props.searchQuery.workCategory,
        work_experience: props.searchQuery.workExperience,
        region: props.searchQuery.region,
      }

      if (
        props.searchQuery.geoLocation?.latitude &&
        props.searchQuery.geoLocation?.longitude
      ) {
        searchQuery.latitude = props.searchQuery.geoLocation.latitude
        searchQuery.longitude = props.searchQuery.geoLocation.longitude
        searchQuery.distance = props.searchQuery.geoLocation.distance
      }

      await createJobAlert({
        payload: {
          frequency: 'D',
          active: true,
          email: state.email,
          searchQuery,
          labels: { location: props.searchQuery.geoLocation?.label },
        },
        domainSlug: props.context.domainSlug,
        baseTooUrl: props.context.baseTooUrl,
      })

      alertCreated.value = true

      // Submit gtm event
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'jrUniversalJobAlertSubscription',
      })
    } catch (err) {
      showRequestError.value = true
      console.error(err)
    } finally {
      isLoading.value = false
    }
  } else {
    emailInput.value.focus()
  }
}
function handleInput() {
  showRequestError.value = false
  v$.value.email.$reset()
}

const reset = () => {
  alertCreated.value = false
  isLoading.value = false
  showRequestError.value = false
  state.email = ''
  v$.value.$reset()
}

const nonce = usePageContext().nonce
useHead(
  () => {
    // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
    // because v-bind generates style="..." and that's not allowed with CSP enabled.

    let color = 'default'
    if (props.item.button?.length) {
      color = props.item.button[0].color
    }

    const style = [
      {
        key: props.item._uid,
        nonce,
        innerHTML: `
          #form${props.item._uid} {
            --btn-color: var(--${colorCustomClass(color, null, '')});
            --btn-text: var(--${colorCustomClass('', color, '')});
          }
      `,
      },
    ]

    return {
      style,
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>
